import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Save, Moon, Book, Heart, MapPin } from 'lucide-react';
import { toast } from 'sonner';

export default function SeniorReligionSettings() {
  const { seniorId } = useParams();
  const navigate = useNavigate();
  const [senior, setSenior] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prayerTimes, setPrayerTimes] = useState(null);
  
  const [formData, setFormData] = useState({
    location: { latitude: '', longitude: '' },
    islamic_settings: {
      enable_prayer_times: true,
      enable_adhan: false,
      enable_quran_verses: true,
      enable_stories: true,
      calculation_method: 'MWL'
    }
  });

  useEffect(() => {
    fetchSeniorData();
  }, [seniorId]);

  const fetchSeniorData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/seniors/${seniorId}`);
      const seniorData = response.data;
      setSenior(seniorData);
      
      // Initialize form data
      if (seniorData.location) {
        setFormData(prev => ({
          ...prev,
          location: seniorData.location
        }));
      }
      
      if (seniorData.islamic_settings) {
        setFormData(prev => ({
          ...prev,
          islamic_settings: seniorData.islamic_settings
        }));
      }
      
      // Fetch prayer times if location is set
      if (seniorData.location && seniorData.location.latitude && seniorData.religion === 'islam') {
        fetchPrayerTimes();
      }
    } catch (error) {
      console.error('Error fetching senior data:', error);
      toast.error('Fehler beim Laden der Profildaten');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrayerTimes = async () => {
    try {
      const response = await axios.get(`${API}/religion/prayertimes?senior_id=${seniorId}`);
      setPrayerTimes(response.data);
    } catch (error) {
      console.error('Error fetching prayer times:', error);
      // Don't show error toast, just log it
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/religion/settings/${seniorId}`, formData);
      toast.success('Religiöse Einstellungen erfolgreich gespeichert!');
      
      // Refresh prayer times if location changed
      if (formData.location.latitude && formData.location.longitude) {
        await fetchPrayerTimes();
      }
    } catch (error) {
      console.error('Error saving religion settings:', error);
      toast.error('Fehler beim Speichern der Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  const handleLocationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      location: {
        ...prev.location,
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const handleIslamicSettingToggle = (setting, value) => {
    setFormData(prev => ({
      ...prev,
      islamic_settings: {
        ...prev.islamic_settings,
        [setting]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!senior || senior.religion !== 'islam') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card>
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-semibold mb-2">Islamisches Modul nicht aktiviert</h3>
            <p className="text-gray-600 mb-4">Diese Einstellungen sind nur für islamische Profile verfügbar.</p>
            <Button onClick={() => navigate(`/seniors/${seniorId}`)}>
              Zurück zum Profil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="outline"
          onClick={() => navigate(`/seniors/${seniorId}`)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Moon className="w-6 h-6 text-indigo-600" />
              Islamische Einstellungen - {senior.name}
            </CardTitle>
            <CardDescription>
              Gebetszeiten, Quran-Verse und religiöse Geschichten konfigurieren
            </CardDescription>
          </CardHeader>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Location Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Standort
              </CardTitle>
              <CardDescription>Für Gebetszeiten-Berechnung erforderlich</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="latitude">Breitengrad (Latitude)</Label>
                  <Input
                    id="latitude"
                    type="number"
                    step="0.0001"
                    value={formData.location.latitude}
                    onChange={(e) => handleLocationChange('latitude', e.target.value)}
                    placeholder="z.B. 51.4556"
                  />
                </div>
                <div>
                  <Label htmlFor="longitude">Längengrad (Longitude)</Label>
                  <Input
                    id="longitude"
                    type="number"
                    step="0.0001"
                    value={formData.location.longitude}
                    onChange={(e) => handleLocationChange('longitude', e.target.value)}
                    placeholder="z.B. 7.0116"
                  />
                </div>
              </div>
              <p className="text-sm text-gray-500">
                💡 Tipp: Verwenden Sie Google Maps, um die Koordinaten zu finden
              </p>
            </CardContent>
          </Card>

          {/* Prayer Times Preview */}
          {prayerTimes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Moon className="w-5 h-5 text-purple-600" />
                  Heutige Gebetszeiten
                </CardTitle>
                <CardDescription>📅 {prayerTimes.date}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">Fajr</p>
                    <p className="text-xl font-bold">{prayerTimes.fajr}</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">Dhuhr</p>
                    <p className="text-xl font-bold">{prayerTimes.dhuhr}</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">Asr</p>
                    <p className="text-xl font-bold">{prayerTimes.asr}</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">Maghrib</p>
                    <p className="text-xl font-bold">{prayerTimes.maghrib}</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">Isha</p>
                    <p className="text-xl font-bold">{prayerTimes.isha}</p>
                  </div>
                  <div className="text-center p-3 bg-indigo-100 rounded-lg">
                    <p className="text-sm text-indigo-600">Nächstes Gebet</p>
                    <p className="text-xl font-bold">{prayerTimes.next_prayer}</p>
                    <p className="text-sm text-indigo-600">{prayerTimes.next_prayer_time}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Islamic Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Modul-Einstellungen</CardTitle>
              <CardDescription>Aktivieren oder deaktivieren Sie islamische Funktionen</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Gebetszeiten anzeigen</p>
                  <p className="text-sm text-gray-500">Tägliche Gebetszeiten basierend auf Standort</p>
                </div>
                <Switch
                  checked={formData.islamic_settings.enable_prayer_times}
                  onCheckedChange={(checked) => handleIslamicSettingToggle('enable_prayer_times', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Adhan aktivieren</p>
                  <p className="text-sm text-gray-500">Ton-Benachrichtigung zu Gebetszeiten (Phase 4)</p>
                </div>
                <Switch
                  checked={formData.islamic_settings.enable_adhan}
                  onCheckedChange={(checked) => handleIslamicSettingToggle('enable_adhan', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Quran-Verse</p>
                  <p className="text-sm text-gray-500">Zufällige Verse in Deutsch & Arabisch</p>
                </div>
                <Switch
                  checked={formData.islamic_settings.enable_quran_verses}
                  onCheckedChange={(checked) => handleIslamicSettingToggle('enable_quran_verses', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Religiöse Geschichten</p>
                  <p className="text-sm text-gray-500">Geschichten über Propheten und Moral</p>
                </div>
                <Switch
                  checked={formData.islamic_settings.enable_stories}
                  onCheckedChange={(checked) => handleIslamicSettingToggle('enable_stories', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Calculation Method */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Berechnungsmethode</CardTitle>
              <CardDescription>Methode zur Berechnung der Gebetszeiten</CardDescription>
            </CardHeader>
            <CardContent>
              <Select
                value={formData.islamic_settings.calculation_method}
                onValueChange={(value) => handleIslamicSettingToggle('calculation_method', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MWL">Muslim World League</SelectItem>
                  <SelectItem value="ISNA">Islamic Society of North America</SelectItem>
                  <SelectItem value="Makkah">Umm Al-Qura University, Makkah</SelectItem>
                  <SelectItem value="Egypt">Egyptian General Authority</SelectItem>
                  <SelectItem value="Turkey">Diyanet İşleri Başkanlığı, Turkey</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex gap-2">
            <Button type="submit" disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Speichere...' : 'Einstellungen speichern'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate(`/seniors/${seniorId}`)}>
              Abbrechen
            </Button>
          </div>
        </form>

        {/* Security Note */}
        <Card className="mt-6 border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <p className="text-sm text-amber-800">
              ⚠️ <strong>Wichtig:</strong> Dieses System stellt nur Informationen dar und gibt keine theologischen 
              oder rechtlichen Urteile ab. Für Fragen zu religiösen Angelegenheiten wenden Sie sich bitte an qualifizierte Gelehrte.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
